import {
    TableContainer,
    tableContainerClasses,
    TableCell,
    Table,
    TableHead,
    TableRow,
    TableBody,
    useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { RowData, ColumnDef, useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../../atoms';

interface TableProps<K extends RowData> {
    data: K[];
    columns: ColumnDef<K>[];
    tableLoader?: boolean;
}

export const StyledTableContainer = styled(TableContainer)(() => ({
    position: 'relative',
    minHeight: '350px',
    [`&.${tableContainerClasses.root}`]: {
        width: '100%'
    }
}));

// FTDS table styling mirrored from TableWithCustomRender (variant="ftds").
const StyledTable = styled(Table)(({ theme }) => ({
    tableLayout: 'auto',
    width: '100%',
    '&.MuiTable-root': {
        borderCollapse: 'separate'
    },
    '& .MuiTableHead-root .MuiTableCell-head': {
        fontWeight: 700,
        lineHeight: '19px',
        fontSize: '14px',
        color: theme.palette.neutral[950],
        height: '51px',
        borderTop: 'none',
        borderBottom: 'none',
        backgroundColor: theme.palette?.semantic?.bg?.raised,
        '&:first-of-type': {
            borderTopLeftRadius: '10px'
        },
        '&:last-of-type': {
            borderTopRightRadius: '10px'
        }
    },
    '& .MuiTableBody-root tr:last-child td:first-of-type': {
        borderBottomLeftRadius: '10px'
    },
    '& .MuiTableBody-root tr:last-child td:last-child': {
        borderBottomRightRadius: '10px'
    }
}));

const NoDataContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '300px',
    color: theme.palette.semantic.text.muted,
    fontSize: '1rem'
}));

export default function FinancialAdvisorTable<K>({ data, columns, tableLoader }: TableProps<K>) {
    const theme = useTheme();
    const { t } = useTranslation();
    const gridBorderColor = theme.palette.neutral[200];
    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel()
    });

    // Grid borders on every cell, dropping the outermost left/right verticals to match FTDS design.
    const headerBorderSx = () => ({
        borderTop: 'none',
        borderBottom: 'none',
        borderLeft: `1px solid ${gridBorderColor}`,
        borderRight: `1px solid ${gridBorderColor}`
    });
    const cellBorderSx = () => ({
        border: `1px solid ${gridBorderColor}`,
    });

    return (
        <StyledTableContainer>
            {tableLoader ? (
                <Spinner enabled={true} position="absolute" />
            ) : data.length === 0 ? (
                <NoDataContainer>{t('TEXT_NO_DATA_FOUND')}</NoDataContainer>
            ) : (
                <StyledTable stickyHeader size="small">
                    <TableHead sx={{ height: 64 }}>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableCell
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        sx={{
                                            verticalAlign: 'middle',
                                            ...headerBorderSx()
                                        }}
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableHead>
                    <TableBody>
                        {table.getRowModel().rows.map((row) => (
                            <TableRow key={row.id}>
                                {row.getVisibleCells().map((cell) => (
                                    <TableCell
                                        key={cell.id}
                                        sx={{
                                            verticalAlign: 'middle',
                                            fontSize: '16px',
                                            fontWeight: 400,
                                            lineHeight: '22px',
                                            color: theme.palette.neutral[950],
                                            ...cellBorderSx()
                                        }}
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </StyledTable>
            )}
        </StyledTableContainer>
    );
}
